import React from "react";
import JoditEditor from "jodit-react";
import { withRouter } from "../../hooks/withRouter";
import { getWistiaConfig, getPost, getEducationalPosts } from "../../api";
import publisherStore from "../../flux/publisherStore";
import { showLovableToast } from "../../components/common/LovableToast/showLovableToast";
import PostEditorSidebar from "./PostEditorSidebar";
import PostEditorPreview from "./PostEditorPreview";
import {
  PostEditorAside,
  PostEditorBackLink,
  PostEditorCard,
  PostEditorCardBody,
  PostEditorCardDesc,
  PostEditorCardHead,
  PostEditorCardIcon,
  PostEditorCardTitle,
  PostEditorColumn,
  PostEditorError,
  PostEditorEyebrow,
  PostEditorHeroActions,
  PostEditorLayout,
  PostEditorLead,
  PostEditorLoading,
  PostEditorMain,
  PostEditorMasthead,
  PostEditorOutlineBtn,
  PostEditorPreviewBtn,
  PostEditorStudioLink,
  PostEditorTitle,
} from "./PostEditor.styles";
import "./PostEditor.scss";

const SUGGESTED_PAGE_SIZE = 10;
const FEATURED_CROP_WIDTH = 1600;
const FEATURED_CROP_HEIGHT = 900;

const emptyVideoUpload = {
  active: false,
  progress: 0,
  fileName: "",
  error: "",
};

const Icon = ({ children }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

const AlignLeftIcon = () => (
  <Icon>
    <line x1="3" x2="21" y1="6" y2="6" />
    <line x1="3" x2="15" y1="12" y2="12" />
    <line x1="3" x2="18" y1="18" y2="18" />
  </Icon>
);
const ImageIcon = () => (
  <Icon>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="m21 15-5-5L5 21" />
  </Icon>
);
const FilmIcon = () => (
  <Icon>
    <rect x="2" y="2" width="20" height="20" rx="2.18" />
    <line x1="7" x2="7" y1="2" y2="22" />
    <line x1="17" x2="17" y1="2" y2="22" />
    <line x1="2" x2="22" y1="12" y2="12" />
  </Icon>
);
const PlusIcon = () => (
  <Icon>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </Icon>
);
const CheckIcon = () => (
  <Icon>
    <path d="M20 6 9 17l-5-5" />
  </Icon>
);
const EyeIcon = () => (
  <Icon>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </Icon>
);
const XIcon = () => (
  <Icon>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </Icon>
);
const LockIcon = () => (
  <Icon>
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </Icon>
);
const ArrowLeftIcon = () => (
  <Icon>
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </Icon>
);

const CardSection = ({ title, description, icon, children }) => (
  <PostEditorCard>
    <PostEditorCardHead>
      <PostEditorCardIcon>{icon}</PostEditorCardIcon>
      <div>
        <PostEditorCardTitle>{title}</PostEditorCardTitle>
        {description ? (
          <PostEditorCardDesc>{description}</PostEditorCardDesc>
        ) : null}
      </div>
    </PostEditorCardHead>
    <PostEditorCardBody>{children}</PostEditorCardBody>
  </PostEditorCard>
);

const editorConfig = {
  uploader: {
    url: `${process.env.REACT_APP_API_URL}media/image`,
    isSuccess: function (resp) {
      return true;
    },
    process: function (resp) {
      return {
        files: [resp.url],
        path: resp.url,
        baseurl: resp.url,
        error: false,
        message: "",
      };
    },
    defaultHandlerSuccess: function (data) {
      const editor = this.jodit || this;
      editor.selection.insertImage(data.baseurl);
    },
  },
  spellcheck: true,
  toolbarAdaptive: false,
  // Match site dark/light chrome; popups mount under body with themed CSS.
  theme:
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "light"
      ? "default"
      : "dark",
  // Keep popups on document.body so toolbar menus aren't clipped by cards.
  popupRoot:
    typeof document !== "undefined" ? document.body : undefined,
  // Hide Jodit's hover "break" line + enter icon between blocks.
  addNewLine: false,
  addNewLineOnDBLClick: false,
  buttons:
    "bold,strikethrough,underline,italic,eraser,ul,ol,outdent,indent,image,video,font,fontsize,brush,paragraph,file,table,link,align,undo,redo,preview",
  placeholder: "",
  readOnly: false,
};

const excerptConfig = {
  spellcheck: false,
  toolbarAdaptive: false,
  theme:
    typeof document !== "undefined" &&
    document.documentElement.getAttribute("data-theme") === "light"
      ? "default"
      : "dark",
  popupRoot:
    typeof document !== "undefined" ? document.body : undefined,
  addNewLine: false,
  addNewLineOnDBLClick: false,
  buttons: "paragraph,bold,italic,underline,eraser,ul,ol,link",
  showCharsCounter: false,
  showWordsCounter: false,
  showXPathInStatusbar: false,
  placeholder: "",
  readOnly: false,
};

class PostEditorPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      title: "",
      slug: "",
      description: "",
      content: "",
      image: "",
      contentType: false,
      wistiaConfig: null,
      embeddedVideo: null,
      publishers: [],
      suggestedPosts: [],
      suggestedPublisher: "",
      suggestedPostsSearchResults: [],
      suggestedSearchTotal: 0,
      suggestedSearchPage: 0,
      suggestedSearchLoading: false,
      suggestedSearchLoadingMore: false,
      searchLine: "",
      src: null,
      selectedFileName: "",
      cropZoom: 1,
      cropX: 50,
      cropY: 50,
      preview: false,
      videoPreviewOpen: false,
      shellReady: false,
      postLoaded: !props.match?.params?.postId,
      publicationSnapshot: null,
      loadError: "",
      videoUpload: { ...emptyVideoUpload },
    };
    this.excerptEditorRef = React.createRef();
    this.contentEditorRef = React.createRef();
    this.imageInputRef = React.createRef();
    this.rootRef = React.createRef();
    this.sidebarRef = React.createRef();
    this.onPublisherStoreChange = this.onPublisherStoreChange.bind(this);
  }

  onPublisherStoreChange() {
    this.setState({
      publishers: publisherStore.getPublishers() || [],
    });
  }

  componentDidMount = async () => {
    this._isMounted = true;
    publisherStore.addChangeListener(this.onPublisherStoreChange);
    this.setState({
      shellReady: true,
      publishers: publisherStore.getPublishers() || [],
    });
    const postId = this.props.match?.params?.postId;

    let wistiaConfig;
    try {
      wistiaConfig = await getWistiaConfig();
    } catch (err) {
      wistiaConfig = null;
    }
    if (!this._isMounted) return;

    const configData = wistiaConfig && wistiaConfig.data;
    this.setState({ wistiaConfig: configData });
    if (configData) {
      this.initVideoUpload(configData);
    }

    this.fetchSuggestedPosts({ page: 0, append: false, searchString: "" });

    if (!postId) {
      this.setState({ postLoaded: true });
      return;
    }

    try {
      const post = await getPost(postId);
      if (!this._isMounted) return;

      const suggestedIds = Array.isArray(post.data?.suggestedPostIds)
        ? post.data.suggestedPostIds
        : [];
      const posts = suggestedIds.map(async (suggestedId) => {
        let suggestedData = await getPost(suggestedId);
        return suggestedData.data;
      });
      const suggestedPostsData = (await Promise.all(posts)).filter(Boolean);
      if (!this._isMounted) return;

      this.setState({
        post: post.data,
        postId: postId,
        publicationSnapshot: {
          id: post.data.id,
          isDraft: post.data.isDraft,
          publishedAt: post.data.publishedAt,
        },
        title: post.data.title,
        description: post.data.excerpt,
        content: post.data.content,
        tags: post.data.tags,
        contentType: post.data.contentTypeId === 2,
        croppedImageUrl: post.data.image && post.data.image.original,
        suggestedPosts: suggestedPostsData,
        adminSelectedPublisherId:
          (post.data.publisher && post.data.publisher.id) ||
          (post.data.author && post.data.author.id) ||
          null,
        postLoaded: true,
      });
    } catch (err) {
      if (!this._isMounted) return;
      this.setState({
        loadError: "This post could not be loaded.",
        postLoaded: true,
      });
    }
  };

  toggleSuggestedPost = (post) => {
    const postId = post && post.id;
    if (!postId) return;

    const posts = this.state.suggestedPosts;
    const alreadySelected = posts.some((p) => p.id === postId);

    this.setState({
      suggestedPosts: alreadySelected
        ? posts.filter((p) => p.id !== postId)
        : [...posts, post],
    });
  };

  isSuggestedSelected = (postId) =>
    this.state.suggestedPosts.some((p) => p.id === postId);

  componentDidUpdate = (prevProps, prevState) => {
    let publishers = this.getPublishers();
    if (
      prevState.publishers.length === 0 &&
      publishers &&
      publishers.length !== 0
    ) {
      this.setState({ publishers: publishers });
    }
  };

  componentWillUnmount() {
    this._isMounted = false;
    publisherStore.removeChangeListener(this.onPublisherStoreChange);
    this.suggestedSearchRequestId = (this.suggestedSearchRequestId || 0) + 1;
    if (this.suggestedSearchTimer) {
      clearTimeout(this.suggestedSearchTimer);
    }
  }

  buildSuggestedFilter = (searchString = "") => ({
    authorIds:
      this.state.suggestedPublisher !== ""
        ? [this.state.suggestedPublisher]
        : [],
    educationOnly: false,
    searchString: searchString || "",
  });

  fetchSuggestedPosts = async ({
    page = 0,
    append = false,
    searchString = this.state.searchLine,
  } = {}) => {
    const requestId = (this.suggestedSearchRequestId || 0) + 1;
    this.suggestedSearchRequestId = requestId;

    if (!this._isMounted) return;

    this.setState({
      suggestedSearchLoading: !append,
      suggestedSearchLoadingMore: append,
    });

    try {
      const res = await getEducationalPosts(
        this.buildSuggestedFilter(searchString),
        SUGGESTED_PAGE_SIZE,
        page
      );

      if (!this._isMounted || requestId !== this.suggestedSearchRequestId) {
        return;
      }

      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      const total =
        typeof res?.data?.total === "number" ? res.data.total : items.length;

      this.setState((prev) => {
        if (!this._isMounted || requestId !== this.suggestedSearchRequestId) {
          return null;
        }

        const existing = append ? prev.suggestedPostsSearchResults : [];
        const seen = new Set(existing.map((p) => p.id));
        const merged = append
          ? [...existing, ...items.filter((p) => p?.id && !seen.has(p.id))]
          : items.filter((p) => p?.id);

        return {
          suggestedPostsSearchResults: merged,
          suggestedSearchTotal: total,
          suggestedSearchPage: page,
          suggestedSearchLoading: false,
          suggestedSearchLoadingMore: false,
        };
      });
    } catch (err) {
      if (!this._isMounted || requestId !== this.suggestedSearchRequestId) {
        return;
      }
      this.setState({
        suggestedSearchLoading: false,
        suggestedSearchLoadingMore: false,
        ...(append
          ? {}
          : { suggestedPostsSearchResults: [], suggestedSearchTotal: 0 }),
      });
    }
  };

  clearSuggested = () => {
    if (this.suggestedSearchTimer) {
      clearTimeout(this.suggestedSearchTimer);
    }
    this.setState({ searchLine: "" }, () => {
      this.fetchSuggestedPosts({ page: 0, append: false, searchString: "" });
    });
  };

  searchSuggested = (event) => {
    const value = event.target.value;
    this.setState({ searchLine: value });

    if (this.suggestedSearchTimer) {
      clearTimeout(this.suggestedSearchTimer);
    }
    this.suggestedSearchTimer = setTimeout(() => {
      this.fetchSuggestedPosts({
        page: 0,
        append: false,
        searchString: value,
      });
    }, 300);
  };

  loadMoreSuggested = () => {
    const {
      suggestedSearchLoading,
      suggestedSearchLoadingMore,
      suggestedPostsSearchResults,
      suggestedSearchTotal,
      suggestedSearchPage,
    } = this.state;

    if (
      suggestedSearchLoading ||
      suggestedSearchLoadingMore ||
      suggestedPostsSearchResults.length >= suggestedSearchTotal
    ) {
      return;
    }

    this.fetchSuggestedPosts({
      page: suggestedSearchPage + 1,
      append: true,
    });
  };

  ensureSuggestedSearch = () => {
    if (
      this.state.suggestedSearchLoading ||
      this.state.suggestedPostsSearchResults.length > 0 ||
      this.state.suggestedSearchTotal > 0
    ) {
      return;
    }
    this.fetchSuggestedPosts({
      page: 0,
      append: false,
      searchString: this.state.searchLine || "",
    });
  };

  closePreview = () => {
    this.setState({ preview: false });
  };

  openPreview = async () => {
    await this.syncEditorsToState();
    // Pull latest Access toggle from sidebar if it hasn't been merged yet.
    const sidebar = this.sidebarRef?.current;
    if (sidebar && typeof sidebar.state?.visibility === "boolean") {
      const contentAvailabilityId = sidebar.state.visibility ? 2 : 1;
      const educationTopics = (sidebar.state.educationalTopics || []).filter(
        (topic) =>
          (sidebar.state.educationalTopicsSelected || []).includes(topic.id)
      );
      const tags = sidebar.state.tags || this.state.post?.tags || this.state.tags;
      await new Promise((resolve) => {
        this.setState(
          {
            post: {
              ...this.state.post,
              contentAvailabilityId,
              educationTopics,
              tags,
            },
            tags,
            preview: true,
            videoPreviewOpen: false,
          },
          resolve
        );
      });
      return;
    }
    this.setState({ preview: true, videoPreviewOpen: false });
  };

  changeSuggestedPublisher = (event) => {
    const value = event.target.value;
    if (this.suggestedSearchTimer) {
      clearTimeout(this.suggestedSearchTimer);
    }
    this.setState(
      {
        suggestedPublisher: value === "" ? "" : parseInt(value, 10),
      },
      () => {
        this.fetchSuggestedPosts({
          page: 0,
          append: false,
          searchString: this.state.searchLine || "",
        });
      }
    );
  };

  setTitle = (event) => {
    this.setState({
      title: event.target.value,
      slug: event.target.value.toLocaleLowerCase().split(" ").join("-"),
    });
  };

  setDescription = (event) => {
    this.setState({ description: event });
  };

  contentType = () => {
    this.setState(
      {
        contentType: !this.state.contentType,
        videoPreviewOpen: false,
      },
      () => {
        if (this.state.contentType) {
          this.initVideoUpload(this.state.wistiaConfig);
        }
      }
    );
  };

  setContent = (event) => {
    this.setState({ content: event });
  };

  setSlug = (event) => {
    this.setState({ slug: event.target.value });
  };

  initVideoUpload = (config) => {
    if (!config || this._wistiaInited) return;
    var self = this;
    window._wapiq = window._wapiq || [];
    window._wapiq.push((W) => {
      window.wistiaUploader = new W.Uploader({
        accessToken: config.accessToken,
        dropIn: "wistia_uploader_post",
        projectId: config.projectId,
        projectName: config.projectName,
      });
      self._wistiaInited = true;
      window.wistiaUploader.bind("uploadstart", (file) => {
        self.setState({
          videoPreviewOpen: false,
          videoUpload: {
            active: true,
            progress: 0,
            fileName: file?.name || "Video",
            error: "",
          },
        });
      });
      window.wistiaUploader.bind("uploadprogress", (file, progress) => {
        const pct = Math.min(100, Math.round(Number(progress) * 100));
        self.setState((prev) => ({
          videoUpload: {
            ...prev.videoUpload,
            active: true,
            progress: Number.isFinite(pct) ? pct : prev.videoUpload.progress,
            fileName: file?.name || prev.videoUpload.fileName,
            error: "",
          },
        }));
      });
      window.wistiaUploader.bind("uploadfailed", () => {
        self.setState({
          videoUpload: {
            ...emptyVideoUpload,
            error: "Video upload failed",
          },
        });
        showLovableToast("Video upload failed");
      });
      window.wistiaUploader.bind("uploadcancelled", () => {
        self.setState({
          videoUpload: { ...emptyVideoUpload },
        });
      });
      window.wistiaUploader.bind(
        "uploadembeddable",
        (file, media, embedCode) => {
          self.setState(
            {
              embeddedVideo: {
                mediaId: media.id,
                thumbnailUrl: media.thumbnail && media.thumbnail.url,
                embedCode: embedCode,
              },
              videoUpload: { ...emptyVideoUpload },
              videoPreviewOpen: true,
            },
            () => {
              self.setupComposerWistia(media.id);
            }
          );
        }
      );
    });
  };

  reloadContent = (post) => {
    if (!post) return;
    this.setState((prev) => ({
      post: { ...(prev.post || {}), ...post },
      content: post.content != null ? post.content : prev.content,
      title: post.title != null ? post.title : prev.title,
      description:
        post.excerpt != null ? post.excerpt : prev.description,
      publicationSnapshot: {
        id: post.id || (prev.publicationSnapshot && prev.publicationSnapshot.id),
        isDraft:
          typeof post.isDraft === "boolean"
            ? post.isDraft
            : prev.publicationSnapshot && prev.publicationSnapshot.isDraft,
        publishedAt:
          post.publishedAt ||
          (prev.publicationSnapshot && prev.publicationSnapshot.publishedAt) ||
          null,
      },
    }));
  };

  onPublicationSaved = (post) => {
    if (!post || !post.id) return;
    this.setState({
      publicationSnapshot: {
        id: post.id,
        isDraft: post.isDraft,
        publishedAt: post.publishedAt || null,
      },
    });
  };

  clickWistiaFileInput = () => {
    const root = document.getElementById("wistia_uploader_post");
    const input = root?.querySelector('input[type="file"]');
    if (!input) return false;
    input.click();
    return true;
  };

  openVideoPicker = () => {
    if (this.state.videoUpload.active) return;
    if (this.clickWistiaFileInput()) return;
    window.setTimeout(() => {
      this.clickWistiaFileInput();
    }, 80);
  };

  getActiveVideo = () =>
    this.state.embeddedVideo ||
    (this.state.post && this.state.post.embeddedVideo) ||
    null;

  setupComposerWistia = (mediaId) => {
    if (!mediaId) return;
    window._wq = window._wq || [];
    window._wq.push({
      id: mediaId,
      options: { autoPlay: true, playsinline: true },
    });
    if (window.Wistia?.embeds?.setup) {
      window.Wistia.embeds.setup();
    }
  };

  toggleVideoPreview = () => {
    const next = !this.state.videoPreviewOpen;
    this.setState({ videoPreviewOpen: next }, () => {
      if (next) {
        const video = this.getActiveVideo();
        this.setupComposerWistia(video && video.mediaId);
      }
    });
  };

  removeUploadedVideo = () => {
    this.setState({
      embeddedVideo: null,
      videoPreviewOpen: false,
    });
  };

  renderVideoEmbed = (mediaId) => {
    if (!mediaId) return null;
    return (
      <div
        key={mediaId}
        className={"wistia_embed wistia_async_" + mediaId}
      />
    );
  };

  onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        const src = reader.result;
        // Mirror legacy: commit a crop immediately so Submit always has `image`,
        // even if the user never clicks "Use crop".
        this.setState(
          {
            src,
            previousCroppedImageUrl: this.state.croppedImageUrl,
            selectedFileName: file.name,
            cropZoom: 1,
            cropX: 50,
            cropY: 50,
          },
          () => {
            this.applyFeaturedCrop(src);
          }
        );
      });
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  applyFeaturedCrop = (src = this.state.src) => {
    if (!src) return Promise.resolve(null);
    const { cropZoom, cropX, cropY } = this.state;
    return new Promise((resolve) => {
      const source = new Image();
      source.onload = () => {
        const maxEdge = 1600;
        const naturalW = source.naturalWidth;
        const naturalH = source.naturalHeight;
        if (!naturalW || !naturalH) {
          showLovableToast("This image could not be cropped.");
          resolve(null);
          return;
        }
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        if (!context) {
          showLovableToast("This image could not be cropped.");
          resolve(null);
          return;
        }

        // Zoom 1× = full image (no forced crop). Zoom in for 16:9 header crop.
        let croppedImageUrl;
        if (cropZoom <= 1.01) {
          const scale = Math.min(1, maxEdge / Math.max(naturalW, naturalH));
          canvas.width = Math.max(1, Math.round(naturalW * scale));
          canvas.height = Math.max(1, Math.round(naturalH * scale));
          context.drawImage(source, 0, 0, canvas.width, canvas.height);
          croppedImageUrl = canvas.toDataURL("image/jpeg", 0.92);
        } else {
          const outputWidth = FEATURED_CROP_WIDTH;
          const outputHeight = FEATURED_CROP_HEIGHT;
          const targetRatio = outputWidth / outputHeight;
          let cropWidth = naturalW;
          let cropHeight = cropWidth / targetRatio;
          if (cropHeight > naturalH) {
            cropHeight = naturalH;
            cropWidth = cropHeight * targetRatio;
          }
          cropWidth /= cropZoom;
          cropHeight /= cropZoom;
          const centerX = (cropX / 100) * naturalW;
          const centerY = (cropY / 100) * naturalH;
          const sourceX = Math.min(
            naturalW - cropWidth,
            Math.max(0, centerX - cropWidth / 2)
          );
          const sourceY = Math.min(
            naturalH - cropHeight,
            Math.max(0, centerY - cropHeight / 2)
          );
          canvas.width = outputWidth;
          canvas.height = outputHeight;
          context.drawImage(
            source,
            sourceX,
            sourceY,
            cropWidth,
            cropHeight,
            0,
            0,
            outputWidth,
            outputHeight
          );
          croppedImageUrl = canvas.toDataURL("image/jpeg", 0.92);
        }
        this.setState({ croppedImageUrl }, () => resolve(croppedImageUrl));
      };
      source.onerror = () => {
        showLovableToast("This image could not be opened.");
        resolve(null);
      };
      source.src = src;
    });
  };

  updateFeaturedCropControl = (patch) => {
    this.setState(patch, () => {
      if (this.state.src) {
        this.applyFeaturedCrop(this.state.src);
      }
    });
  };

  confirmFeaturedCrop = async () => {
    if (!this.state.src) return;
    await this.applyFeaturedCrop(this.state.src);
    this.setState({ src: null, selectedFileName: "" });
  };

  /** Ensure pending crop UI is committed before publish/update (legacy auto-crop). */
  ensureFeaturedImage = async () => {
    if (this.state.src) {
      await this.applyFeaturedCrop(this.state.src);
      await new Promise((resolve) => {
        this.setState({ src: null, selectedFileName: "" }, resolve);
      });
    }
    return this.state.croppedImageUrl;
  };

  prepareBeforeSubmit = async () => {
    await this.syncEditorsToState();
    await this.ensureFeaturedImage();
  };

  discardFeaturedCrop = () => {
    this.setState({
      src: null,
      selectedFileName: "",
      cropZoom: 1,
      cropX: 50,
      cropY: 50,
    });
  };

  removeFeaturedImage = () => {
    const current = this.state.croppedImageUrl || "";
    if (current.indexOf("http") === 0) return;
    this.setState({
      croppedImageUrl: this.state.previousCroppedImageUrl,
      src: null,
      selectedFileName: "",
    });
  };

  addSuggestedPost = (post) => {
    if (!post || !post.id || this.isSuggestedSelected(post.id)) return;
    this.setState({
      suggestedPosts: [...this.state.suggestedPosts, post],
    });
  };

  submitFromHeader = async () => {
    await this.prepareBeforeSubmit();
    if (this.sidebarRef.current && this.sidebarRef.current.submitCurrent) {
      this.sidebarRef.current.submitCurrent();
    }
  };

  removeSuggestedPost = (id) => {
    const posts = this.state.suggestedPosts.filter((p) => p.id !== id);
    this.setState({
      suggestedPosts: posts,
    });
  };

  onTagsUpdate = (tags) => {
    this.setState({ post: { ...this.state.post, tags } });
  };

  getCurrentUser() {
    const user = localStorage.getItem("user");
    return user && JSON.parse(user);
  }

  getPublishers() {
    const publishers = publisherStore.getPublishers();
    return publishers;
  }

  onChangePublisher = (id) => {
    const publishers = this.getPublishers() || [];
    const selected = publishers.find(
      (publisher) => Number(publisher.id) === Number(id)
    );
    this.setState({
      adminSelectedPublisherId: id,
      post: {
        ...this.state.post,
        publisherId: id,
        publisher: selected || this.state.post?.publisher,
        author: selected || this.state.post?.author,
      },
    });
  };

  getPublisherForPreview() {
    const post = this.state && this.state.post;
    const publishers = this.getPublishers() || [];
    const findById = (id) =>
      publishers.find(
        (publisher) => Number(publisher.id) === Number(id)
      );

    // Prefer full publisher records from the store (avatars live there).
    const fromPost =
      (post && (post.publisher || post.author)) || null;
    if (fromPost?.id) {
      const hydrated = findById(fromPost.id);
      if (hydrated) return hydrated;
      if (fromPost.avatar || fromPost.name) return fromPost;
    }

    const selectedId =
      this.state.adminSelectedPublisherId ||
      (post && post.publisherId) ||
      (post && post.authorId) ||
      (fromPost && fromPost.id);

    if (selectedId) {
      const selected = findById(selectedId);
      if (selected) return selected;
    }

    const currentUser = this.getCurrentUser();
    if (currentUser?.id) {
      const asPublisher = findById(currentUser.id);
      if (asPublisher) return asPublisher;

      // Analysts often match the logged-in user shape.
      if (currentUser.avatar || currentUser.name) {
        return currentUser;
      }
    }

    return fromPost;
  }

  isUserAdmin = () => {
    const user = this.getCurrentUser();
    const isAdmin = user && user.userRoleId === 1;
    return isAdmin;
  };

  onSidebarUpdate = (sidebarState) => {
    this.setState({ post: { ...this.state.post, ...sidebarState } });
  };

  onPostCreation = (id) => {
    this.setState({ post: { ...this.state.post, id } });
  };

  onStatusChange = (isDraft) => {
    this.setState({ post: { ...this.state.post, isDraft } });
  };

  setExcerptJodit = (editor) => {
    this.excerptJodit = editor;
  };

  setContentJodit = (editor) => {
    this.contentJodit = editor;
  };

  /** Live editor HTML. State updates on blur only, so save and preview read the editor directly. */
  getLiveExcerpt = () => {
    if (this.excerptJodit && typeof this.excerptJodit.value === "string") {
      return this.excerptJodit.value;
    }
    return this.state.description || "";
  };

  getLiveContent = () => {
    if (this.contentJodit && typeof this.contentJodit.value === "string") {
      return this.contentJodit.value;
    }
    return this.state.content || "";
  };

  syncEditorsToState = () =>
    new Promise((resolve) => {
      this.setState(
        {
          description: this.getLiveExcerpt(),
          content: this.getLiveContent(),
        },
        resolve
      );
    });

  renderPreview() {
    if (!this.state.preview) return null;
    const { post, croppedImageUrl } = this.state;
    const publisher = this.getPublisherForPreview();
    const previewImage = croppedImageUrl
      ? { original: croppedImageUrl, small: croppedImageUrl }
      : post && post.image;
    const liveContent = this.getLiveContent();
    const liveExcerpt = this.getLiveExcerpt();
    const contentTypeId = this.state.contentType ? 2 : 1;
    const embeddedVideo =
      this.state.embeddedVideo || (post && post.embeddedVideo) || null;
    const publishedAt =
      (post && (post.publishedAt || post.createdAt)) ||
      new Date().toISOString();

    // Members see full body when logged in; empty string still "defined"
    // so PostDetailContent does not treat the post as locked.
    const contentForMember =
      liveContent != null && liveContent !== ""
        ? liveContent
        : contentTypeId === 2
          ? liveContent || ""
          : liveContent || (post && post.content) || "";

    const contentAvailabilityId =
      (post && post.contentAvailabilityId != null
        ? post.contentAvailabilityId
        : null) ?? 2;

    const previewPost = {
      ...(post || {}),
      id: (post && post.id) || "preview",
      title: this.state.title || "Untitled post",
      content: contentForMember,
      excerpt: liveExcerpt || "",
      contentTypeId,
      contentAvailabilityId,
      embeddedVideo,
      croppedImageUrl,
      image: previewImage || null,
      publisher: publisher || null,
      author: publisher || (post && post.author) || {},
      tags: (post && post.tags) || this.state.tags || [],
      educationTopics: (post && post.educationTopics) || [],
      likes: (post && post.likes) || { total: 0, likedByCurrentUser: false },
      isFavourite: Boolean(post && post.isFavourite),
      publishedAt,
      url: (post && post.url) || "/blog/preview",
    };

    return (
      <PostEditorPreview
        post={previewPost}
        suggestedPosts={this.state.suggestedPosts || []}
        user={this.getCurrentUser()}
        onClose={this.closePreview}
      />
    );
  }

  renderMasthead() {
    const { postId } = this.props.match?.params || {};
    const createdId = this.state.post && this.state.post.id;
    const isEdit = Boolean(postId || createdId);
    return (
      <>
        <PostEditorStudioLink to="/cms">
          <ArrowLeftIcon />
          Content studio
        </PostEditorStudioLink>
        <PostEditorMasthead>
          <div>
            <PostEditorEyebrow>
              {isEdit ? "Edit post" : "New post"}
            </PostEditorEyebrow>
            <PostEditorTitle>
              {isEdit ? "Refine the signal" : "Publish with conviction"}
            </PostEditorTitle>
            <PostEditorLead>
              {isEdit
                ? "Update the live post without changing its core format."
                : "Shape the analysis, presentation, and member access in one workspace."}
            </PostEditorLead>
          </div>
          <PostEditorHeroActions>
            <PostEditorOutlineBtn type="button" onClick={this.openPreview}>
              <EyeIcon />
              Preview post
            </PostEditorOutlineBtn>
            <PostEditorPreviewBtn
              type="button"
              onClick={this.submitFromHeader}
            >
              {isEdit ? "Update" : "Submit"}
            </PostEditorPreviewBtn>
          </PostEditorHeroActions>
        </PostEditorMasthead>
      </>
    );
  }

  renderFeaturedCard() {
    const { src, croppedImageUrl } = this.state;
    const canRemove =
      croppedImageUrl && String(croppedImageUrl).indexOf("http") === -1;
    return (
      <CardSection
        title="Featured image"
        description="Upload, crop to 16:9, and confirm the post header."
        icon={<ImageIcon />}
      >
        <input
          ref={this.imageInputRef}
          type="file"
          accept="image/*"
          className="post-editor__file-input"
          onChange={this.onSelectFile}
        />
        {src ? (
          <div className="post-editor__crop-panel">
            <div className="post-editor__crop-stage">
              <img
                src={src}
                alt=""
                draggable={false}
                style={{
                  objectPosition: `${this.state.cropX}% ${this.state.cropY}%`,
                  transform: `scale(${this.state.cropZoom})`,
                  transformOrigin: `${this.state.cropX}% ${this.state.cropY}%`,
                }}
              />
            </div>
            <div className="post-editor__crop-controls">
              <label>
                Zoom <em>{Number(this.state.cropZoom).toFixed(1)}×</em>
                <input
                  type="range"
                  min="1"
                  max="3"
                  step="0.1"
                  value={this.state.cropZoom}
                  onChange={(event) =>
                    this.updateFeaturedCropControl({
                      cropZoom: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label>
                Horizontal
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={this.state.cropX}
                  onChange={(event) =>
                    this.updateFeaturedCropControl({
                      cropX: Number(event.target.value),
                    })
                  }
                />
              </label>
              <label>
                Vertical
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={this.state.cropY}
                  onChange={(event) =>
                    this.updateFeaturedCropControl({
                      cropY: Number(event.target.value),
                    })
                  }
                />
              </label>
            </div>
            <div className="post-editor__crop-actions">
              <button
                type="button"
                className="post-editor__crop-apply"
                onClick={this.confirmFeaturedCrop}
              >
                <i className="material-icons" aria-hidden="true">
                  crop
                </i>
                Use crop
              </button>
              <button
                type="button"
                className="post-editor__crop-discard-btn"
                onClick={this.discardFeaturedCrop}
              >
                Discard
              </button>
            </div>
          </div>
        ) : croppedImageUrl ? (
          <div className="post-editor__header-preview">
            <img src={croppedImageUrl} alt="" />
            <div className="post-editor__header-bar">
              <span>Header preview</span>
              <div className="post-editor__header-bar-actions">
                <button
                  type="button"
                  className="post-editor__ghost"
                  onClick={() => this.imageInputRef.current?.click()}
                >
                  <i className="material-icons" aria-hidden="true">
                    file_upload
                  </i>
                  Replace
                </button>
                {canRemove && (
                  <button
                    type="button"
                    className="post-editor__ghost post-editor__ghost--danger"
                    aria-label="Remove featured image"
                    onClick={this.removeFeaturedImage}
                  >
                    <i className="material-icons">delete</i>
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="post-editor__dropzone"
            onClick={() => this.imageInputRef.current?.click()}
          >
            <i className="material-icons" aria-hidden="true">
              file_upload
            </i>
            Choose featured image
          </button>
        )}
      </CardSection>
    );
  }

  renderVideoCard() {
    const post = this.state.post;
    const isVideo =
      this.state.contentType || (post && post.contentTypeId === 2);
    if (!isVideo) return null;
    const previousVideo = post && post.embeddedVideo;
    const uploadedVideo = this.state.embeddedVideo;
    const activeVideo = uploadedVideo || previousVideo;
    const videoUploading = this.state.videoUpload.active;
    const videoUploadPct = this.state.videoUpload.progress || 0;
    const hasVideo = Boolean(activeVideo);
    const previewOpen = this.state.videoPreviewOpen;

    return (
      <CardSection
        title="Post video"
        description="Upload a video file. Links are not accepted."
        icon={<FilmIcon />}
      >
        {videoUploading ? (
          <div className="post-editor__video-card">
            <div className="post-editor__video-thumb" />
            <div className="post-editor__video-meta">
              <p>{this.state.videoUpload.fileName || "Uploading video"}</p>
              <span>
                {videoUploadPct < 100 ? `${videoUploadPct}%` : "Processing"}
              </span>
              <span className="post-editor__video-progress">
                <span
                  style={{
                    width: `${videoUploadPct < 100 ? videoUploadPct : 100}%`,
                  }}
                />
              </span>
            </div>
          </div>
        ) : hasVideo ? (
          <div className="post-editor__video-block">
            <div className="post-editor__video-card">
              <button
                type="button"
                className={`post-editor__video-thumb${
                  previewOpen ? " is-open" : ""
                }`}
                aria-label={
                  previewOpen ? "Hide video preview" : "Toggle video preview"
                }
                onClick={this.toggleVideoPreview}
              >
                {activeVideo.thumbnailUrl ? (
                  <img src={activeVideo.thumbnailUrl} alt="" />
                ) : (
                  <i className="material-icons" aria-hidden="true">
                    movie
                  </i>
                )}
                <span className="post-editor__video-thumb-overlay">
                  <EyeIcon />
                </span>
              </button>
              <div className="post-editor__video-meta">
                <p>
                  {uploadedVideo
                    ? previousVideo
                      ? "Replacement video attached"
                      : "Video attached"
                    : "Current post video"}
                </p>
                <span>Click the thumbnail to preview.</span>
              </div>
              <button
                type="button"
                className="post-editor__add-btn"
                onClick={this.openVideoPicker}
              >
                Replace
              </button>
              {uploadedVideo ? (
                <button
                  type="button"
                  className="post-editor__video-remove"
                  aria-label="Remove video"
                  onClick={this.removeUploadedVideo}
                >
                  <XIcon />
                </button>
              ) : null}
            </div>
            {previewOpen ? (
              <div className="post-editor__video-preview">
                <div className="post-editor__video-preview-head">
                  <i className="material-icons" aria-hidden="true">
                    movie
                  </i>
                  <span>Video preview</span>
                  <button
                    type="button"
                    aria-label="Hide video preview"
                    onClick={() => this.setState({ videoPreviewOpen: false })}
                  >
                    <i className="material-icons">close</i>
                  </button>
                </div>
                <div className="post-editor__video-preview-player">
                  {this.renderVideoEmbed(activeVideo.mediaId)}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <button
            type="button"
            className="post-editor__dropzone"
            onClick={this.openVideoPicker}
          >
            <i className="material-icons" aria-hidden="true">
              movie
            </i>
            Choose video file
          </button>
        )}
        {this.state.videoUpload.error ? (
          <p className="post-editor__empty">{this.state.videoUpload.error}</p>
        ) : null}
      </CardSection>
    );
  }

  renderSuggestedCard() {
    return (
      <CardSection
        title="Suggested posts"
        description="Search by author or title, then choose related reading."
        icon={<PlusIcon />}
      >
        {this.state.suggestedPosts.length > 0 && (
          <div className="post-editor__chips">
            {this.state.suggestedPosts.map((item) => (
              <span key={item.id} className="post-editor__chip">
                <span className="post-editor__post-title">
                  {item.title || "Untitled"}
                </span>
                <button
                  type="button"
                  className="post-editor__chip-remove"
                  aria-label={`Remove ${item.title || "post"}`}
                  onClick={() => this.removeSuggestedPost(item.id)}
                >
                  <i className="material-icons">close</i>
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="post-editor__suggested-filters">
          <select
            className="post-editor__select"
            onChange={this.changeSuggestedPublisher}
            value={this.state.suggestedPublisher || ""}
            onFocus={this.ensureSuggestedSearch}
          >
            <option value="">All authors</option>
            {this.state.publishers.map((publisher) => (
              <option key={publisher.id} value={publisher.id}>
                {publisher.name}
              </option>
            ))}
          </select>
          <div className="post-editor__search-wrap">
            <i className="material-icons" aria-hidden="true">
              search
            </i>
            <input
              className="post-editor__input"
              placeholder="Search by title..."
              onChange={this.searchSuggested}
              onFocus={this.ensureSuggestedSearch}
              value={this.state.searchLine}
            />
          </div>
        </div>
        {this.state.suggestedSearchLoading ? (
          <p className="post-editor__empty">Loading posts...</p>
        ) : this.state.suggestedPostsSearchResults.length === 0 ? (
          <p className="post-editor__empty">
            Search or pick an author to find related articles.
          </p>
        ) : (
          <>
            <div className="post-editor__results">
              {this.state.suggestedPostsSearchResults.map((item) => {
                const selected = this.isSuggestedSelected(item.id);
                return (
                  <div key={item.id} className="post-editor__result">
                    <div className="post-editor__thumb">
                      <img src={item.image?.small || ""} alt="" />
                    </div>
                    <div className="min-w-0" style={{ minWidth: 0, flex: 1 }}>
                      <span className="post-editor__post-title">
                        {item.title || "Untitled"}
                      </span>
                      <span className="post-editor__post-sub">
                        {item.author?.name || "Unknown"}
                        {item.publishedAt ? ` · ${item.publishedAt}` : ""}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="post-editor__add-btn"
                      disabled={selected}
                      onClick={() => this.addSuggestedPost(item)}
                    >
                      {selected ? <CheckIcon /> : <PlusIcon />}
                      {selected ? "Added" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
            {this.state.suggestedPostsSearchResults.length <
              this.state.suggestedSearchTotal && (
              <button
                type="button"
                className="post-editor__load-more"
                onClick={this.loadMoreSuggested}
                disabled={this.state.suggestedSearchLoadingMore}
              >
                {this.state.suggestedSearchLoadingMore
                  ? "Loading..."
                  : "Load more"}
              </button>
            )}
          </>
        )}
      </CardSection>
    );
  }

  render() {
    const { postId } = this.props.match?.params || {};
    const editorsReady =
      this.state.shellReady && (!postId || this.state.postLoaded);

    if (this.state.loadError) {
      return (
        <PostEditorMain>
          <PostEditorError>
            <h2>Could not open this post</h2>
            <p>{this.state.loadError}</p>
            <PostEditorBackLink to="/cms" style={{ marginTop: 16 }}>
              Back to CMS
            </PostEditorBackLink>
          </PostEditorError>
        </PostEditorMain>
      );
    }

    return (
      <PostEditorMain>
        <div id="post-editor-root" className="post-editor" ref={this.rootRef}>
          {this.renderPreview()}
          <div id="wistia_uploader_post" />
          {this.renderMasthead()}

          {!editorsReady ? (
            <PostEditorLoading>
              <span aria-hidden="true" />
              Loading editor…
            </PostEditorLoading>
          ) : (
            <PostEditorLayout>
              <PostEditorColumn>
                <fieldset className="post-editor__lock-fields">
                <CardSection
                  title="Post format"
                  description={
                    postId
                      ? "Content type is locked after creation."
                      : "Choose how members experience this post."
                  }
                  icon={<AlignLeftIcon />}
                >
                  <div
                    className="post-editor__seg"
                    role="group"
                    aria-label="Content type"
                  >
                    <button
                      type="button"
                      className={`post-editor__seg-btn${
                        !this.state.contentType ? " is-active" : ""
                      }`}
                      disabled={Boolean(postId)}
                      onClick={() => {
                        if (!postId && this.state.contentType) this.contentType();
                      }}
                    >
                      Text
                    </button>
                    <button
                      type="button"
                      className={`post-editor__seg-btn${
                        this.state.contentType ? " is-active" : ""
                      }`}
                      disabled={Boolean(postId)}
                      onClick={() => {
                        if (!postId && !this.state.contentType)
                          this.contentType();
                      }}
                    >
                      Video
                    </button>
                  </div>
                  {postId && (
                    <div className="post-editor__lock">
                      <LockIcon />
                      Locked as {this.state.contentType ? "Video" : "Text"}
                    </div>
                  )}
                  {!postId && (
                    <div className="post-editor__field" style={{ marginTop: 20 }}>
                      <label htmlFor="post-slug" className="post-editor__label">
                        Slug
                      </label>
                      <input
                        id="post-slug"
                        className="post-editor__input"
                        value={this.state.slug}
                        onChange={this.setSlug}
                        placeholder="Slug"
                        aria-label="Slug"
                      />
                    </div>
                  )}
                </CardSection>

                <CardSection
                  title="Story"
                  description="Write the headline, summary, and complete post."
                  icon={<AlignLeftIcon />}
                >
                  <div className="post-editor__field">
                    <label htmlFor="post-title">Title</label>
                    <input
                      id="post-title"
                      className="post-editor__input post-editor__input--title"
                      placeholder="Post title"
                      onChange={this.setTitle}
                      value={this.state.title}
                    />
                  </div>
                  <div className="post-editor__field">
                    <label htmlFor="post-excerpt">Excerpt</label>
                    <div className="post-editor__jodit post-editor__jodit--excerpt">
                      <JoditEditor
                        ref={this.excerptEditorRef}
                        editorRef={this.setExcerptJodit}
                        value={this.state.description || ""}
                        config={excerptConfig}
                        tabIndex={1}
                        onBlur={this.setDescription}
                      />
                    </div>
                  </div>
                  <div className="post-editor__field">
                    <label htmlFor="post-content">Body</label>
                    <div className="post-editor__jodit post-editor__jodit--body">
                      <JoditEditor
                        ref={this.contentEditorRef}
                        editorRef={this.setContentJodit}
                        value={this.state.content || ""}
                        config={editorConfig}
                        tabIndex={1}
                        onBlur={this.setContent}
                      />
                    </div>
                  </div>
                </CardSection>

                {this.renderFeaturedCard()}
                {this.renderVideoCard()}
                {this.renderSuggestedCard()}
                </fieldset>
              </PostEditorColumn>

              <PostEditorAside>
                <PostEditorSidebar
                  ref={this.sidebarRef}
                  onChangePublisher={this.onChangePublisher}
                  onSidebarUpdate={this.onSidebarUpdate}
                  onPostCreation={this.onPostCreation}
                  onTagsUpdate={this.onTagsUpdate}
                  reloadContent={this.reloadContent}
                  savedPublication={this.state.publicationSnapshot}
                  onPublicationSaved={this.onPublicationSaved}
                  onPreview={this.openPreview}
                  onBeforeSubmit={this.prepareBeforeSubmit}
                  getFeaturedImage={() =>
                    this.state.croppedImageUrl === undefined
                      ? this.state.url
                      : this.state.croppedImageUrl
                  }
                  getExcerpt={this.getLiveExcerpt}
                  getContent={this.getLiveContent}
                  suggested={this.state.suggestedPosts}
                  onSelectEducationalTopic={(selectedTopics, sidebarState) =>
                    this.setState({
                      post: {
                        ...this.state.post,
                        ...sidebarState,
                        educationTopics: selectedTopics,
                      },
                    })
                  }
                  post={this.state.post}
                  title={this.state.title}
                  description={this.state.description}
                  content={this.state.content}
                  image={
                    this.state.croppedImageUrl === undefined
                      ? this.state.url
                      : this.state.croppedImageUrl
                  }
                  type={this.state.contentType}
                  video={this.state.embeddedVideo}
                />
              </PostEditorAside>
            </PostEditorLayout>
          )}
        </div>
      </PostEditorMain>
    );
  }
}

export default withRouter(PostEditorPage);
